import api from './api';
import type { GoodsReceipt, GoodsReceiptLine, PaginatedResponse } from '../types/venue';

/**
 * Build the GR base path. When `venueId` is provided we use the venue-scoped
 * mount; when omitted we fall back to the admin-only global mount (kept for
 * any future cross-venue admin UI; nothing currently uses the omitted form).
 */
const buildBase = (venueId?: string) =>
  venueId ? `/admin/venue/${venueId}/goods-receipt` : '/admin/goods-receipt';

export interface PostReceiptResult {
  message: string;
  receiptId: string;
  movementsCreated: number;
  poStatus: string;
}

const goodsReceiptService = {
  getReceipts: async (
    venueId: string | undefined,
    purchaseOrderId: string,
  ): Promise<PaginatedResponse<GoodsReceipt>> => {
    const response = await api.get(buildBase(venueId), { params: { purchaseOrderId, pageSize: 100 } });
    return response.data;
  },

  getReceiptLines: async (
    venueId: string | undefined,
    receiptId: string,
  ): Promise<PaginatedResponse<GoodsReceiptLine>> => {
    const response = await api.get(`${buildBase(venueId)}/${receiptId}/line`, { params: { pageSize: 500 } });
    return response.data;
  },

  createReceipt: async (
    venueId: string,
    data: {
      purchaseOrderId: string;
      locationId: string;
      receivedDate: string;
      receivedBy: string;
      notes?: string;
      ownershipAccountId?: string;
    },
  ): Promise<GoodsReceipt> => {
    // Body venueId is ignored on the venue-scoped mount — controller derives
    // venueId from req.params.venueId.
    const response = await api.post(buildBase(venueId), data);
    return response.data;
  },

  createReceiptLine: async (
    venueId: string,
    receiptId: string,
    data: {
      purchaseOrderLineId: string;
      inventoryItemId: string;
      receivedQty: number;
      unitCostAtReceipt?: number;
      /** Phase A: lot number for lot-tracked items. Auto-generated if omitted. */
      lotNumber?: string;
      supplierBatchRef?: string;
      expiryDate?: string;
    },
  ): Promise<GoodsReceiptLine> => {
    const response = await api.post(`${buildBase(venueId)}/${receiptId}/line`, data);
    return response.data;
  },

  /** Fix 6: create all receipt lines in one request. displayName/locationName are server-side. */
  createReceiptLinesBatch: async (
    venueId: string,
    receiptId: string,
    lines: Array<{
      purchaseOrderLineId: string;
      inventoryItemId: string;
      receivedQty: number;
      unitCostAtReceipt?: number;
      /** Phase A: lot number for lot-tracked items. Auto-generated if omitted. */
      lotNumber?: string;
      supplierBatchRef?: string;
      expiryDate?: string;
    }>,
  ): Promise<GoodsReceiptLine[]> => {
    const response = await api.post(`${buildBase(venueId)}/${receiptId}/lines/batch`, lines);
    return response.data;
  },

  postReceipt: async (venueId: string, receiptId: string): Promise<PostReceiptResult> => {
    const response = await api.post(`${buildBase(venueId)}/${receiptId}/post`);
    return response.data;
  },
};

export default goodsReceiptService;
